import { ConstraintBuilders } from './constraints';
import { ObjectiveBuilders } from './objectives';
import { getDefaultConfig, getPresetConfig } from './configPresets';

// Build the JSON payload for the solver API from the optimization file.
// Modular by design: constraints and objective are selected via config.
//
// Config shape (optional):
// {
//   constraints: [ { key: 'capacity', enabled: true, params: {} }, ... ],
//   objective: { key: 'maximizeCoverage', params: {} } | null
// }
export function buildSolverPayload(optimizationFile, config) {
  // Resolve config precedence: explicit config > presetKey in file > default
  const resolved = config
    || (optimizationFile?.presetKey ? getPresetConfig(optimizationFile.presetKey) : null)
    || getDefaultConfig();
  const idx = optimizationFile?.optimizationIndexes || {};
  const decisionVars = idx?.decisionVariables || {};
  const eventDecisionVars = idx?.eventRoleDecisionVariables || {};
  const externalUseVars = idx?.externalUseDecisionVariables || {};
  const externalGatingVars = idx?.externalGatingDecisionVariables || {};
  const dayWorkVars = idx?.dayWorkDecisionVariables || {};
  const breakPairVars = idx?.adjacencyPairDecisionVariables || {};
  const seriesDistinctVars = idx?.seriesDistinctDecisionVariables || {};

  // Variables: binary domain for each decision variable
  const variables = [
    // Assignment variables (binary)
    ...Object.keys(decisionVars).map((id) => ({ id, domain: [0, 1] })),
    // Event-role slack variables (continuous/integer range 0..1000)
    ...Object.keys(eventDecisionVars).map((id) => ({ id, domain: [0, 1000] })),
    // External use indicator variables (binary)
    ...Object.keys(externalUseVars).map((id) => ({ id, domain: [0, 1] })),
    // External gating variables (binary)
    ...Object.keys(externalGatingVars).map((id) => ({ id, domain: [0, 1] })),
    // Day-work indicator variables (binary)
    ...Object.keys(dayWorkVars).map((id) => ({ id, domain: [0, 1] })),
    // Break-pair indicators (binary)
    ...Object.keys(breakPairVars).map((id) => ({ id, domain: [0, 1] })),
    // Series-distinct usage indicators (binary)
    ...Object.keys(seriesDistinctVars).map((id) => ({ id, domain: [0, 1] })),
  ];

  // Constraints: compose from enabled builders
  const constraints = [];
  for (const entry of resolved.constraints || []) {
    if (!entry?.enabled) continue;
    const builder = ConstraintBuilders[entry.key];
    if (typeof builder === 'function') {
      const built = builder(optimizationFile, entry.params || {});
      if (Array.isArray(built) && built.length) constraints.push(...built);
    }
  }

  // Objective: optional and pluggable
  let objective = null;
  if (resolved.objective?.key) {
    const objBuilder = ObjectiveBuilders[resolved.objective.key];
    if (typeof objBuilder === 'function') {
      objective = objBuilder(optimizationFile, resolved.objective.params || {});
    }
  }

  const payload = { variables, constraints };
  if (objective) payload.objective = objective;
  return payload;
}
